/* eslint prettier/prettier: ["error", { printWidth: 1000 }] */
import { ProductsTab } from '../components/AdminProducts/AdminProducts';
import { productResources } from '../resources/builders/productResources';
import { IResource } from '../resources/resource';
import { userDownloadsResources } from '../resources/builders/userDownloadsResources';
import { organizationResources } from '../resources/builders/organizationResources';
import { productReleaseResources } from '../resources/builders/productReleaseResources';
import { organizationProductResources } from '../resources/builders/organizationProductResources';
import { userResources } from '../resources/builders/userResources';
import { roleResources } from '../resources/builders/roleResources';
import { licenseResources } from '../resources/builders/licenseResources';
import { cloudLiveResources } from '../resources/builders/cloudLiveResources';
import { scheduledResources } from '../resources/builders/scheduledResources';
import { PendingJobType } from '../models/CronJob';
import { eventResources } from '../resources/builders/eventResources';
import { overviewResources } from '../resources/builders/overviewResources';
import { documentationResources } from '../resources/builders/documentationResources';
import { activityResources } from '../resources/builders/activityResources';
import { useContext, useMemo } from 'react';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { ArgsType } from '../types/ArgsType';
import { IBasicOrganization } from '../resources/models/IOrganization';
import { AuthContext } from '../contexts/AuthContext';
import { Strictify } from '../types/Strictify';
import { zendeskResources } from '../resources/builders/zendeskResources';

export const authenticatedRoot = '/panel';

const encode = (key: string, value?: string) => {
  return value ? encodeURIComponent(value) : `:${key}`;
};

export const AuthenticatedRoutes = {
  ChooseOrganization: () => `${authenticatedRoot}/choose-organization`,

  ViewAccount: () => `${authenticatedRoot}/account`,

  NoOrganization: () => `${authenticatedRoot}/no-organization`,

  // Organization
  CurrentOrganizationOverview: () => `${authenticatedRoot}/organization/overview`,
  CurrentOrganizationOverviewProduct: (product?: string) => `${authenticatedRoot}/organization/overview/${product ?? ':product'}`,
  CurrentOrganizationUsers: () => `${authenticatedRoot}/organization/users`,
  CurrentOrganizationDetails: () => `${authenticatedRoot}/organization/details`,
  CurrentOrganizationProducts: () => `${authenticatedRoot}/organization/products`,
  CurrentOrganizationProduct: (customer_product_id?: number) => `${authenticatedRoot}/organization/products/${customer_product_id ?? ':customer_product_id'}`,
  CurrentOrganizationDealHistory: (organization_id?: string, product_id?: number) => `${authenticatedRoot}/organization/${organization_id ?? ':organization_id'}/products/${product_id ?? ':product_id'}/deal-history`,
  CurrentOrganizationInvites: () => `${authenticatedRoot}/organization/invites`,

  CurrentOrganizationCloudLive: () => `${authenticatedRoot}/organization/cloud-live`,

  Licenses: () => `${authenticatedRoot}/licenses`,
  AllLicenses: () => `${authenticatedRoot}/all-licenses`,

  Support: () => `${authenticatedRoot}/support`,

  // Global
  GlobalOverview: () => `${authenticatedRoot}/overview`,
  GlobalOverviewProduct: (product?: string) => `${authenticatedRoot}/overview/${product ?? ':product'}`,

  AdminProducts: () => `${authenticatedRoot}/products`,
  AdminProductsTab: (tab?: ProductsTab) => `${authenticatedRoot}/products/${tab ?? ':tab'}`,

  Releases: () => `${authenticatedRoot}/releases`,
  ReleasesProduct: (productId?: number) => `${authenticatedRoot}/releases/${productId ?? ':productId'}`,
  ReleasesProductVersion: (productId?: number, version?: string) => `${authenticatedRoot}/releases/${productId ?? ':productId'}/version/${version ?? ':version'}`,
  Organizations: () => `${authenticatedRoot}/organizations`,

  OrganizationDetails: (organization_name?: string) => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}`,
  // `${authenticatedRoot}/organizations/${organization_name ?? ':organization_name'}`,
  OrganizationDetailsProducts: (organization_name?: string) => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/products`,
  OrganizationDetailsUsers: (organization_name?: string) => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/users`,
  OrganizationDetailsInvites: (organization_name?: string) => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/invites`,
  OrganizationDetailsTickets: (organization_name?: string) => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/tickets`,
  OrganizationDetailsLive: (organization_name?: string) => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/live`,
  OrganizationProduct: (organization_name?: string, customer_product_id?: number) => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/products/${customer_product_id ?? ':customer_product_id'}`,

  AllUsers: () => `${authenticatedRoot}/users`,
  User: (user_id?: string) => `${authenticatedRoot}/users/${user_id ?? ':user_id'}`,
  UserRoles: (user_id?: string) => `${authenticatedRoot}/users/${user_id ?? ':user_id'}/roles`,
  UserOrganization: (organization_name?: string, user_id?: string) => `${authenticatedRoot}/users/${user_id ?? ':user_id'}/organization/${encode('organization_name', organization_name)}`,
  UserDownloads: () => `${authenticatedRoot}/downloads`,
  UserDownloadsProduct: (product?: number) => `${authenticatedRoot}/downloads/${product ?? ':product'}`,
  UserDownloadsProductLens: (product?: number) => `${authenticatedRoot}/downloads/${product ?? ':product'}/lens`,
  UserDownloadsProductVersion: (product?: number, version?: string) => `${authenticatedRoot}/downloads/${product ?? ':product'}/${version ?? ':version'}`,
  UserDownloadsProductExamples: (product?: number, version?: string) => `${authenticatedRoot}/downloads/${product ?? ':product'}/${version ?? ':version'}/examples`,
  UserDownloadsProductContent: (product?: number, version?: string) => `${authenticatedRoot}/downloads/${product ?? ':product'}/${version ?? ':version'}/content`,
  UserDownloadsProductTools: (product?: number, version?: string) => `${authenticatedRoot}/downloads/${product ?? ':product'}/${version ?? ':version'}/tools`,
  UserDownloadsProductInstaller: (product?: number, version?: string, installer?: string) => `${authenticatedRoot}/downloads/${product ?? ':product'}/${version ?? ':version'}/${installer ?? ':installer'}`,
  Documentation: () => `${authenticatedRoot}/docs`,
  DocumentationProduct: (product?: number) => `${authenticatedRoot}/docs/${product ?? ':product'}`,
  Admins: () => `${authenticatedRoot}/admins`,

  CloudLive: () => `${authenticatedRoot}/cloud-live`,

  Jobs: () => `${authenticatedRoot}/jobs`,
  JobsType: (job_type?: PendingJobType) => `${authenticatedRoot}/jobs/${job_type ?? ':job_type'}`,

  Events: () => `${authenticatedRoot}/api-events`,

  Activity: () => `${authenticatedRoot}/activity`,
  ActivityItem: (ip?: string) => `${authenticatedRoot}/activity/${ip ?? ':ip'}`,

  Unverified: () => `${authenticatedRoot}/unverified`,
};

type OptArr<T> = T | T[];

type RouteResources = {
  [RouteName in keyof typeof AuthenticatedRoutes]: (...args: Strictify<ArgsType<typeof AuthenticatedRoutes[RouteName]>>) => OptArr<IResource<any, any> | null> | boolean;
};

const anyOrg = {
  id: 'any',
  name: 'any',
} as IBasicOrganization;

export const useRouteResources = () => {
  const { organization } = useContext(OrganizationContext);
  const { identity } = useContext(AuthContext);
  return useMemo<RouteResources>(
    () => ({
      ChooseOrganization: () => true,
      ViewAccount: () => true,
      NoOrganization: () => true,
      CurrentOrganizationOverview: () => (organization ? overviewResources.getProductsList(organization) : null),
      CurrentOrganizationOverviewProduct: () => (organization ? overviewResources.getProductsList(organization) : null),
      CurrentOrganizationUsers: () => (organization ? organizationResources.listOrganizationUsers(organization.name) : null),
      CurrentOrganizationDetails: () => (organization ? organizationResources.updateCurrentOrganizationByName(organization.name) : null),
      CurrentOrganizationProducts: () => (organization ? organizationProductResources.getOrganizationProducts(organization) : null),
      CurrentOrganizationProduct: (customer_product_id) => (organization ? organizationProductResources.getOrganizationProductLicenses(organization, customer_product_id) : null),
      CurrentOrganizationDealHistory: (organization_id, product_id) => (organization ? organizationProductResources.getDealHistory(organization, product_id) : null),
      CurrentOrganizationInvites: () => (organization ? organizationResources.getInvitations(organization.name) : null),
      CurrentOrganizationCloudLive: () => (organization ? cloudLiveResources.getOrganizationLiveStacks(organization, 'any') : null),
      GlobalOverview: () => overviewResources.getSummaryExpirations(),
      GlobalOverviewProduct: () => overviewResources.getSummaryExpirations(),
      AdminProducts: () => productResources.getProducts(),
      AdminProductsTab: () => productResources.getProducts(),
      Releases: () => productReleaseResources.getProductReleases(0),
      ReleasesProduct: (product) => productReleaseResources.getProductReleases(product),
      ReleasesProductVersion: (product, version) => productReleaseResources.updateProductReleaseVersion(product, version),
      Organizations: () => organizationResources.getOrganizations(),
      OrganizationDetails: (organization_name) => organizationResources.getOrganizationByName(organization_name),
      OrganizationDetailsProducts: () => organizationProductResources.getOrganizationProducts(anyOrg),
      OrganizationDetailsUsers: (organization_name) => organizationResources.listOrganizationUsers(organization_name),
      OrganizationDetailsInvites: (organization_name) => organizationResources.getInvitations(organization_name),
      OrganizationDetailsTickets: () => false,
      OrganizationDetailsLive: () => cloudLiveResources.getOrganizationLiveStacks(anyOrg, 'any'),
      OrganizationProduct: (org, product) => organizationProductResources.getOrganizationProductLicenses(anyOrg, product),
      Licenses: () => (organization && identity ? licenseResources.getUserLicenses(organization, identity.userId) : null),
      AllLicenses: () => (organization ? licenseResources.getOrganizationLicenses(organization) : null),
      Support: () => (organization && identity ? zendeskResources.getUserTickets(identity.userId, 'any') : null),
      AllUsers: () => userResources.getUsers(),
      User: (user_id) => userResources.getUser(user_id),
      UserRoles: (user_id) => [userResources.getUserInfo(user_id), roleResources.listRoles(), organizationResources.getOrganizations()],
      UserOrganization: (organization_name, user_id) => organizationResources.getUser(organization_name, user_id),
      UserDownloads: () => (organization ? userDownloadsResources.listDownloads(organization) : null),
      UserDownloadsProduct: () => (organization ? userDownloadsResources.listDownloads(organization) : null),
      UserDownloadsProductVersion: () => (organization ? userDownloadsResources.getEngineContent(organization) : null),
      UserDownloadsProductExamples: () => (organization ? userDownloadsResources.getExamples(organization) : null),
      UserDownloadsProductContent: () => (organization ? userDownloadsResources.getEngineContent(organization) : null),
      UserDownloadsProductTools: () => (organization ? userDownloadsResources.getTools(organization) : null),
      UserDownloadsProductInstaller: () => userDownloadsResources.getFile(),
      UserDownloadsProductLens: () => (organization ? userDownloadsResources.getLens(organization) : null),
      Documentation: () => (organization ? documentationResources.getDocumentation(organization) : null),
      DocumentationProduct: () => (organization ? documentationResources.getDocumentation(organization) : null),
      Admins: () => userResources.getAdmins(),
      CloudLive: () => cloudLiveResources.getAllLiveStacks('any'),
      Jobs: () => scheduledResources.getAllJobs(),
      JobsType: () => scheduledResources.getAllJobs(),
      Events: () => eventResources.getApiEvents(),
      Activity: () => activityResources.getActivity(),
      ActivityItem: () => activityResources.getIpActivity(),
      Unverified: () => true,
    }),
    [organization, identity]
  );
};
